import { useQuery } from '@apollo/client/react';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { ErrorIndicator } from '@components/ErrorIndicator';
import { Layout } from '@components/Layout';
import { ThankYou } from '@features/ThankYouPage';
import { usePageStateHandler } from '@hooks/usePageStateHandler';
import { useBasketContext } from '@providers/basket/BasketProvider/Context';
import { useDetailsContext } from '@providers/basket/DetailsProvider/Context';
import { withProfiler } from '@services/sentry/withProfiler';
import { notifyParentQuoteSubmitted } from '@utils/parentFrame';
import {
  clearFormPersistStorage,
  removeAddressSessionStorage,
  thankYouDetailsStorage,
} from '@utils/storagePropertyManagement';

import { thankYouQuery } from './query';

const ThankYouPageUnwrapped = () => {
  const { basketId, setApplicationSubmitting } = useBasketContext();
  const { resetDetails } = useDetailsContext();
  const navigate = useNavigate();

  const { data, loading, error } = useQuery(thankYouQuery, {
    variables: { basketId },
  });

  const { isLoading, hasError } = usePageStateHandler({
    loading,
    hasData: !!data?.basket,
    error,
  });

  // Lets an embedding page (Partner App web's iframe) know the quote is submitted.
  useEffect(() => {
    notifyParentQuoteSubmitted(basketId);
  }, [basketId]);

  useEffect(() => {
    resetDetails();
    clearFormPersistStorage();
    removeAddressSessionStorage();
    setApplicationSubmitting(false);
  }, [resetDetails, setApplicationSubmitting]);

  if (isLoading) {
    return null;
  }

  if (hasError) {
    return (
      <Layout title="Thank you" onBack={() => navigate(-1)}>
        <ErrorIndicator />
      </Layout>
    );
  }

  const accountHolderName =
    thankYouDetailsStorage.get()?.primaryFirstName || '';

  const title = accountHolderName
    ? `Welcome, ${accountHolderName}!`
    : 'Thank You';

  return (
    <Layout title={title}>
      <ThankYou data={data ?? null} />
    </Layout>
  );
};

const ThankYouPage = withProfiler(ThankYouPageUnwrapped);

export { ThankYouPage };
